import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const useOfflineDetection = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const offlineToastIdRef = useRef<string | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Dismiss the offline toast when coming back online
      if (offlineToastIdRef.current) {
        toast.dismiss(offlineToastIdRef.current);
        offlineToastIdRef.current = null;
      }
      // Show a brief "back online" message
      toast.success(<p className="toast">You're back online!</p>, {
        duration: 2000,
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      // Only show toast if we don't already have one
      if (!offlineToastIdRef.current) {
        // Show persistent offline toast at bottom left
        const toastId = toast.error(
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 animate-pulse rounded-full bg-red-500"></div>
            <p className="toast">You are offline</p>
          </div>,
          {
            duration: Infinity, // Keep it visible until dismissed
            position: "bottom-left",
            style: {
              background: "#FEF2F2",
              border: "0.5px solid #EF4444",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "12px 16px",
              fontFamily: "Inter",
              fontStyle: "normal",
              fontWeight: "400",
              fontSize: "14px",
              lineHeight: "19px",
              color: "#DC2626",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              zIndex: "2147483647",
              isolation: "isolate",
              transformStyle: "preserve-3d",
              transform: "translateZ(1000px)",
            },
          }
        );
        offlineToastIdRef.current = toastId;
      }
    };

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check initial state and show offline toast if already offline
    if (!navigator.onLine) {
      handleOffline();
    }

    // Cleanup event listeners
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      // Clean up any existing toast
      if (offlineToastIdRef.current) {
        toast.dismiss(offlineToastIdRef.current);
        offlineToastIdRef.current = null;
      }
    };
  }, []); // Empty dependency array to prevent re-running

  return { isOnline };
};

export default useOfflineDetection;
